import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../API/axiosNeatex';
import type { APIResponse } from '@store/Shared/shared';

interface ProjectBudget {
  id: string;
  organizationId: string;
  versionNo: number;
  statusId: string;
  statusName: string;
  statusName2: string;
  createdDate: string;
  notes: string;
}

export interface BudgetLineModels {
  id?: string;
  budgetId: string;
  logicalLineId: string;
  taskId: string;
  costCategory: string;
  amount: number;
  currency: string;
  notes: string;
}

export const createProjectBudget = createAsyncThunk(
  'Project/createProjectBudget',
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<string>>('Project/CreateProjectBudget', item);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProjectBudgetStatus = createAsyncThunk(
  'Project/updateProjectBudgetStatus',
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Project/UpdateProjectBudgetStatus', item);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getBudgetByVersion = createAsyncThunk(
  'Project/getBudgetByVersion',
  async ({ organizationId, versionNo }: { organizationId?: string; versionNo?: number }, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get('Project/GetBudgetByOrgVersion', {
        params: {
          organizationId,
          versionNo
        }
      });

      if (data.succeeded === true) {
        return data.data;
      } else {
        return null;
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// إضافة هذه الدالة في السلايس
export const getBudgetsByOrganization = createAsyncThunk(
  'Project/getBudgetsByOrganization',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Project/GetBudgetByOrgVersion?organizationId=${organizationId}`);
      if (data.succeeded === true) {
        return data.data;
      } else {
        return [];
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getBudgetListByProjectId = createAsyncThunk(
  'Project/getBudgetListByProjectId',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Project/GetBudgetListByProjectId?organizationId=${organizationId}`);
      if (data.succeeded === true) {
        return data.data;
      } else {
        return [];
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const addProjectBudgetLine = createAsyncThunk(
  'Project/addProjectBudgetLine',
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<string>>('Project/AddProjectBudgetLine', item);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProjectBudgetLine = createAsyncThunk(
  'Project/updateProjectBudgetLine',
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Project/UpdateProjectBudgetLine', item);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteProjectBudgetLine = createAsyncThunk(
  'Project/deleteProjectBudgetLine',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Project/DeleteProjectBudgetLine', { id });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getBudgetLinesByBudget = createAsyncThunk(
  'Project/getBudgetLinesByBudget',
  async (budgetId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Project/GetBudgetLinesByBudget?budgetId=${budgetId}`);
      if (data.succeeded === true) {
        return data.data;
      } else {
        return [];
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState: {
  budgetList: ProjectBudget[];
  previousBudgetList: ProjectBudget[];
  loading: boolean;
  error: any | null;
  //
  budgetLines: BudgetLineModels[];
  loading2: boolean;
  error2: any | null;
  //
  currentBudget: ProjectBudget | null;
  loading3: boolean;
  error3: any | null;
  //
  organizationBudgets: ProjectBudget; // إضافة هذا
  loading4: boolean; // إضافة هذا
  error4: any | null; // إضافة هذا
} = {
  budgetList: [],
  previousBudgetList: [],
  loading: false,
  error: null,
  //
  budgetLines: [],
  loading2: false,
  error2: null,
  //
  currentBudget: null,
  loading3: false,
  error3: null,
  //
  organizationBudgets: {} as ProjectBudget, // جديد
  loading4: false, // جديد
  error4: null // جديد
};

const projectBudgetSlice = createSlice({
  name: 'ProjectBudget',
  initialState,
  reducers: {
    updateBudgetInList: (state, action: PayloadAction<{ budgetId: string; updates: Partial<ProjectBudget> }>) => {
      const { budgetId, updates } = action.payload;
      const idx = state.budgetList.findIndex(b => String(b.id) === String(budgetId));
      if (idx !== -1) {
        state.budgetList[idx] = { ...state.budgetList[idx], ...updates };
      }
    },
    updateBudgetLineInList: (state, action: PayloadAction<{ lineId: string; updates: Partial<BudgetLineModels> }>) => {
      const { lineId, updates } = action.payload;
      const idx = state.budgetLines.findIndex(bl => String(bl.id) === String(lineId));
      if (idx !== -1) {
        state.budgetLines[idx] = { ...state.budgetLines[idx], ...updates };
      }
    },
    rollbackBudgetList: state => {
      state.budgetList = state.previousBudgetList;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getBudgetByVersion.pending, state => {
        state.loading3 = true;
      })
      .addCase(getBudgetByVersion.fulfilled, (state, action: PayloadAction<ProjectBudget>) => {
        state.loading3 = false;
        state.currentBudget = action.payload;
      })
      .addCase(getBudgetByVersion.rejected, (state, action: PayloadAction<any>) => {
        state.loading3 = false;
        state.error3 = action.payload;
      })
      //
      .addCase(getBudgetLinesByBudget.pending, state => {
        state.loading2 = true;
      })
      .addCase(getBudgetLinesByBudget.fulfilled, (state, action: PayloadAction<BudgetLineModels[]>) => {
        state.loading2 = false;
        state.budgetLines = action.payload;
      })
      .addCase(getBudgetLinesByBudget.rejected, (state, action: PayloadAction<any>) => {
        state.loading2 = false;
        state.error2 = action.payload;
      })

      // ... الـ cases الحالية
      .addCase(getBudgetsByOrganization.pending, state => {
        state.loading4 = true;
      })
      .addCase(getBudgetsByOrganization.fulfilled, (state, action: PayloadAction<ProjectBudget>) => {
        state.loading4 = false;
        // state.organizationBudgets = action.payload;
        state.organizationBudgets = action.payload;
        // تعيين أول ميزانية كـ currentBudget إذا كانت موجودة
        // if (action.payload.length > 0) {
        state.currentBudget = action.payload;
        // }
      })
      .addCase(getBudgetsByOrganization.rejected, (state, action: PayloadAction<any>) => {
        state.loading4 = false;
        state.error4 = action.payload;
      })
      //
      .addCase(getBudgetListByProjectId.pending, state => {
        state.loading3 = true;
      })
      .addCase(getBudgetListByProjectId.fulfilled, (state, action: PayloadAction<ProjectBudget[]>) => {
        state.loading3 = false;
        state.budgetList = action.payload;
      })
      .addCase(getBudgetListByProjectId.rejected, (state, action: PayloadAction<any>) => {
        state.loading3 = false;
        state.error3 = action.payload;
      });
  }
});

export const { updateBudgetInList, updateBudgetLineInList, rollbackBudgetList } = projectBudgetSlice.actions;
export default projectBudgetSlice.reducer;
